import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Turnstile } from "@/components/security/Turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBotProtection } from "@/hooks/useBotProtection";
import { useTurnstile } from "@/hooks/useTurnstile";
import { authApi } from "../../services/api";

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Bot protection
  const {
    securityHeaders,
    isReady: securityReady,
    resetFormTimer,
  } = useBotProtection();
  const turnstile = useTurnstile();

  // Reset form timer when component mounts
  useEffect(() => {
    resetFormTimer();
  }, [resetFormTimer]);

  // ============================================
  // TanStack Query - Forgot Password Mutation
  // ============================================
  const forgotPasswordMutation = useMutation({
    mutationFn: (emailAddress) => {
      // Include turnstile token if available
      const headers = { ...securityHeaders };
      if (turnstile.token) {
        headers["X-Turnstile-Token"] = turnstile.token;
      }
      return authApi.forgotPassword(emailAddress, headers);
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;

      // Handle challenge required response
      if (errorData?.code === "CHALLENGE_REQUIRED") {
        setError(
          t(
            "auth.forgotPassword.securityCheckRequired",
            "Please complete the security verification",
          ),
        );
        turnstile.reset();
        return;
      }

      // Always show success to prevent email enumeration
      setSuccess(true);
    },
  });

  const loading = forgotPasswordMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(t("auth.validation.emailInvalid"));
      return;
    }

    forgotPasswordMutation.mutate(email);
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("auth.forgotPassword.successTitle")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("auth.forgotPassword.successMessage", { email })}
        </p>
        <Link
          to="/login"
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t("auth.forgotPassword.backToLogin")}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground text-center mb-2">
        {t("auth.forgotPassword.title")}
      </h2>
      <p className="text-muted-foreground text-center mb-6">
        {t("auth.forgotPassword.subtitle")}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.forgotPassword.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            placeholder={t("auth.forgotPassword.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />
        </div>

        {/* Turnstile CAPTCHA - only shows if configured */}
        <Turnstile
          {...turnstile.turnstileProps}
          className="flex justify-center"
        />

        <Button
          type="submit"
          disabled={loading || !securityReady}
          className="w-full"
        >
          {loading
            ? t("auth.forgotPassword.sending")
            : t("auth.forgotPassword.sendLink")}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.register.haveAccount")}{" "}
        <Link
          to="/login"
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t("auth.login.signIn")}
        </Link>
      </p>
    </div>
  );
};

export default ForgotPassword;
